import { Grid, TextField } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { DEFAULT_POSTAL_CODE_REGEXP, defaultFormProps, getValidationProps } from 'util/Form';
import DateFormItem from 'components/shared/DateFormItem';
import ComponentProps from 'store/types/ComponentProps';
import { EventCatalogFilterFormValues } from 'store/types/FormValues';
import CreditTypeFormItem from 'components/events/CreditTypeFormItem';
import {
  EventCatalogHiddenFilters,
  getEventCatalogFilterSearch,
  getEventCatalogFilterValues,
  isSameSearch,
} from 'util/EventCatalogFilter';
import EventTypeFormItem from './EventTypeFormItem';
import DistanceFormItem from './DistanceFormItem';
import LocationFormItem from './LocationFormItem';
import EventCountryFormItem from './EventCountryFormItem';
import { ParametersContext } from 'components/ParametersGuard';

import commonStyles from 'styles/common.module.scss';
import EventAgeFormItem from './EventAgeFormItem';

const EventCatalogFilterBar: React.FunctionComponent<ComponentProps> = ({ className = '' }) => {
  const { search, pathname } = useLocation();
  const history = useHistory();
  const form = useForm<EventCatalogFilterFormValues>({
    ...defaultFormProps,
    defaultValues: getEventCatalogFilterValues(search),
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
  } = form;

  const {
    events: { hideEventTypeFilter, hideCreditTypeFilter, hideCountryFilter, hideAgeFilter },
  } = useContext(ParametersContext);
  const hiddenFilters: EventCatalogHiddenFilters = useMemo(
    () => ({ hideCountryFilter, hideAgeFilter }),
    [hideCountryFilter, hideAgeFilter]
  );

  useEffect(() => {
    const disabledFields: ('country' | 'state' | 'age')[] = [];

    if (hideCountryFilter) disabledFields.push('country', 'state');
    if (hideAgeFilter) disabledFields.push('age');

    const searchParams = new URLSearchParams(search);
    let changed = false;

    disabledFields.forEach((fieldName) => {
      if (getValues(fieldName)) {
        setValue(fieldName, '');
      }
      if (searchParams.has(fieldName)) {
        searchParams.delete(fieldName);
        changed = true;
      }
    });

    if (changed) history.replace({ pathname, search: `?${searchParams.toString()}` });
  }, [hideCountryFilter, hideAgeFilter, search, pathname, history, setValue, getValues]);

  const applyFilters = useCallback(
    (formValues: EventCatalogFilterFormValues) => {
      const newSearch: string = getEventCatalogFilterSearch(formValues, history.location.search, hiddenFilters);

      if (!isSameSearch(newSearch, history.location.search)) {
        history.replace({ pathname, search: newSearch });
      }
    },
    [history, pathname, hiddenFilters]
  );

  useEffect(() => {
    const subscription = watch(() => handleSubmit(() => applyFilters(getValues()))());

    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, applyFilters, getValues]);

  useEffect(() => {
    if (!isSameSearch(getEventCatalogFilterSearch(getValues(), search, hiddenFilters), search)) {
      reset(getEventCatalogFilterValues(search));
    }
  }, [search, getValues, reset, hiddenFilters]);

  return (
    <aside className={className}>
      <FormProvider {...form}>
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps}>
            <h3 className={commonStyles.subTitle}>{'Event Dates'}</h3>
            <DateFormItem fieldName={'startTime'} label={'Start Date'} required={false} />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <DateFormItem fieldName={'endTime'} label={'End Date'} required={false} />
          </Grid>
          {!hideEventTypeFilter && (
            <Grid {...defaultGridItemProps}>
              <h3 className={commonStyles.subTitle}>{'Event Type'}</h3>
              <EventTypeFormItem fieldName={'type'} required={false} />
            </Grid>
          )}
          {!hideCreditTypeFilter && (
            <Grid {...defaultGridItemProps}>
              <h3 className={commonStyles.subTitle}>{'Credit Type'}</h3>
              <CreditTypeFormItem fieldName={'creditType'} required={false} multiple={true} />
            </Grid>
          )}
          <Grid {...defaultGridItemProps}>
            <h3 className={commonStyles.subTitle}>{'Location'}</h3>
            <LocationFormItem fieldName={'location'} />
          </Grid>
          {!hideCountryFilter && <EventCountryFormItem fieldName={'country'} required={false} />}
          {!hideAgeFilter && <EventAgeFormItem fieldName={'age'} required={false} />}
          <Grid {...defaultGridItemProps} className={commonStyles.hidden}>
            <h3 className={commonStyles.subTitle}>{'Distance'}</h3>
            <Grid {...defaultGridContainerProps}>
              <Grid {...defaultGridItemProps} xs={6}>
                <DistanceFormItem />
              </Grid>
              <Grid {...defaultGridItemProps} xs={6}>
                <Controller
                  render={({ field }) => (
                    <TextField {...field} {...getValidationProps('zipCode', errors)} label={'Zip Code'} />
                  )}
                  name={'zipCode'}
                  control={control}
                  rules={{ pattern: { value: DEFAULT_POSTAL_CODE_REGEXP, message: 'Please input valid zip code' } }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
    </aside>
  );
};
export default EventCatalogFilterBar;
